import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import {
	Backdrop,
	Button,
	CircularProgress,
	Grid,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio
} from '@mui/material';
import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { AuthorizerContext } from 'app/providers/acl/AclProvider';

import { Form, ModuleSearchField, useModel, useMountEffect, useSettings, Utils } from 'app/main/components';
import { DescriptionSection, InlineTitle } from 'app/main/modules/components/FastAddDialogSettingsManager';
import InvoicesDetailsSectionInfo from '../sections/InvoicesDetailsSectionInfo';
import InvoicesDetailsSectionRows from '../sections/InvoicesDetailsSectionRows';
import InvoicesDetailsSectionDeadlines from '../sections/InvoicesDetailsSectionDeadlines';

interface InvoicesModuleEditDialogProps {
	invoiceUid?: string;
	baseModel?: any;
	newRowBaseModel?: any;
	updateRefresh?: VoidFunction;
	isAdd?: boolean;
}

export const showInvoicesModuleEditDialog = (props: InvoicesModuleEditDialogProps) =>
	openDialog({
		children: <InvoicesModuleEditDialog {...props} />,
		fullWidth: true,
		maxWidth: 'lg'
	});

export default function InvoicesModuleEditDialog(props: InvoicesModuleEditDialogProps) {
	const { settings, t } = useSettings('invoices');
	const dispatch = useDispatch();
	const authorizer = React.useContext(AuthorizerContext);

	const [isLoading, setIsLoading] = React.useState(false);

	const [insertType, setInsertType] = React.useState(props.isAdd ? 'add' : 'link');
	const [invoiceUid, setInvoiceUid] = React.useState(props.invoiceUid);

	const isAdd = insertType === 'add';

	const { model, setModel, resetModifiedModel, sendChange, save } = useModel({
		baseModel: { invc_uid: invoiceUid || uuidv4(), ...(isAdd ? props.baseModel || {} : {}) },
		settings,
		isEdit: true,
		isAdd
	});

	const updateModel = React.useCallback(
		(uid?: string, data?: any) => {
			setModel(data || {});
			resetModifiedModel({ invc_uid: uid });
		},
		[resetModifiedModel, setModel]
	);

	function handleInsertTypeChange(_event: React.ChangeEvent<HTMLInputElement>, newValue: string) {
		setInsertType(newValue);
		updateModel();
	}

	const executeSave = () => {
		save({
			onSuccess: () => {
				dispatch(Utils.alertSuccessMsg(t, isAdd ? 'ALERT_ADD_SUCCESS' : 'ALERT_UPDATE_SUCCESS'));
				dispatch(closeDialog());
				props.updateRefresh?.();
			}
		});
	};

	useMountEffect(() => {
		if (invoiceUid && !isAdd) {
			setIsLoading(true);
			Utils.executeStored({
				action: 'st_invoices_get_details',
				params: { invc_uid: invoiceUid },
				dispatch,
				successHandler: ({ data }) => {
					updateModel(data.invc_uid, data);
					setIsLoading(false);
				}
			});
		}
	});

	const isDetailsEdit = !props.isAdd || isAdd || !!invoiceUid;

	return (
		<>
			<Backdrop className="z-9999" open={isLoading}>
				<CircularProgress disableShrink />
			</Backdrop>
			<DialogTitle>
				{t(props.isAdd ? 'DIALOG_TITLE_INVOICES_MODULE_ADD_TITLE' : 'DIALOG_TITLE_INVOICES_MODULE_EDIT_TITLE')}
			</DialogTitle>
			<DialogContent className="py-0">
				<Form id="invoicesModuleEditDialog" onSubmit={executeSave}>
					{props.isAdd && (
						<Grid container alignItems="center" spacing={1}>
							<Grid size={{ md: 6, xs: 12 }}>
								<FormControl className="px-8">
									<RadioGroup
										row
										name="insertType"
										value={insertType}
										onChange={handleInsertTypeChange}
									>
										<FormControlLabel
											value="add"
											control={<Radio />}
											label={t('DETAILS_INVOICES_OPTION_NEW_INVOICE')}
										/>
										<FormControlLabel
											value="link"
											control={<Radio />}
											label={t('DETAILS_INVOICES_OPTION_LINK_INVOICE')}
										/>
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid size={{ md: 6, xs: 12 }}>
								<ModuleSearchField
									name="invc_uid"
									label={t('MODULE_INVOICES_NAME_SINGULAR')}
									module="invoices"
									uid={invoiceUid}
									getOptionLabel={option =>
										`${option.invc_title}${option.comp_title ? ` - ${option.comp_title}` : ''}`
									}
									isEdit={insertType === 'link'}
									sendChange={(_, newValue) => setInvoiceUid(newValue)}
									afterChange={newValue => updateModel(newValue?.invc_uid, newValue)}
									required={insertType === 'link'}
									validationError={t('ERROR_TEXT_REQUIRED')}
								/>
							</Grid>
						</Grid>
					)}
					<Grid container spacing={1}>
						<InlineTitle text="DETAILS_INFO_SECTION" />
						<InvoicesDetailsSectionInfo model={model} isEdit={isDetailsEdit} sendChange={sendChange} />
						<InlineTitle text="DETAILS_DESCRIPTION_SECTION" />
						<DescriptionSection
							name="invc_descriptionhtml"
							value={model.invc_descriptionhtml}
							isEdit={isDetailsEdit}
							sendChange={sendChange}
						/>
						<InlineTitle text="INVOICES_ROWS_SECTION_TITLE" />
						<Grid size={12}>
							<InvoicesDetailsSectionRows
								model={model}
								isEdit={isDetailsEdit}
								newRowBaseModel={props.newRowBaseModel}
								sendChange={sendChange}
							/>
						</Grid>
						{authorizer.can('view', 'invoices_deadlines') && (
							<>
								<InlineTitle text={'INVOICES_DEADLINES_SECTION_TITLE'} />
								<Grid size={12}>
									<InvoicesDetailsSectionDeadlines
										model={model}
										isEdit={isDetailsEdit}
										newRowBaseModel={props.newRowBaseModel}
										sendChange={sendChange}
									/>
								</Grid>
							</>
						)}
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="invoicesModuleEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
